import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@atom/components/common/dragAndDrop';
import SchemaDetailContext, {
  DragDropType,
} from '@atom/components/schemaDetail/SchemaDetailContext';
import { ELEMENT_UPDATE } from '@atom/graph/schema';
import { List } from '@atom/mui';
import {
  ElementUpdateInput,
  SchemaTree,
  SchemaTreeAttributeGroup,
} from '@atom/types/schema';

import AttributeGroupRow from './AttributeGroupRow';
import DetailHeader from './DetailHeader';
import {
  mergePendingSchemaChanges,
  reorderAttributeGroups,
  reorderAttributes,
} from './subItemDetailUtilities';

const styles = {
  list: {
    padding: 0,
    minHeight: '1rem',
  },
};

const SubItemDetail = () => {
  const container = useRef<HTMLDivElement>(null);

  const {
    schemaTree,
    selectedSubItem,
    refetchSchemaTree,
    setSelectedSubItem,
    selectedAttributeRoute,
    setSelectedAttributeRoute,
    pendingSchemaUpdates,
    setPendingSchemaUpdates,
    pendingCreations,
  } = useContext(SchemaDetailContext);

  // If pending changes exist, they will be merged with the schema data
  const mergedSubItem = mergePendingSchemaChanges(
    selectedSubItem,
    pendingSchemaUpdates,
  );

  const [createdAttributeGroupId, setCreatedAttributeGroupId] =
    useState<string>();

  const [updateElement] = useMutation<
    { elementUpdate: SchemaTree },
    { input: ElementUpdateInput }
  >(ELEMENT_UPDATE);

  useEffect(() => {
    // Gets newly created group and scrolls to it.
    const lastGroupId = R.last(selectedSubItem.attributeGroups)?.id;

    if (lastGroupId === createdAttributeGroupId) {
      container?.current?.scrollIntoView({ behavior: 'smooth' });
      setCreatedAttributeGroupId(null);
    }
  }, [selectedSubItem, createdAttributeGroupId]);

  const onDragEnd = async (result: DropResult) => {
    const { destination, type } = result;

    if (!destination) {
      return;
    }

    const { updatedAttributeGroups, payload } =
      type === DragDropType.ATTRIBUTE_GROUP
        ? reorderAttributeGroups(mergedSubItem, result)
        : reorderAttributes(mergedSubItem, result);

    // If the schema is already published, add the changes to cart
    if (schemaTree?.isPublished) {
      setPendingSchemaUpdates(prevUpdates => {
        return {
          ...prevUpdates,
          [payload.schemaId]: updatedAttributeGroups,
        };
      });
      return;
    }

    setSelectedSubItem({
      ...mergedSubItem,
      attributeGroups: updatedAttributeGroups,
    });

    // If an attribute is currently selected, upgrade its route to account for
    // an attribute being moved from one group to another
    if (selectedAttributeRoute) {
      setSelectedAttributeRoute({
        attributeId: result.draggableId,
        attributeGroupId: result.destination.droppableId,
      });
    }

    await updateElement({
      variables: {
        input: payload,
      },
    });

    refetchSchemaTree();
  };

  const isDragDropDisabled = R.has(selectedSubItem.id)(pendingCreations);

  return (
    <>
      <DetailHeader setCreatedAttributeGroupId={setCreatedAttributeGroupId} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId="subItemDetail"
          type={DragDropType.ATTRIBUTE_GROUP}
          isDropDisabled={isDragDropDisabled}
        >
          <List style={styles.list}>
            {mergedSubItem.attributeGroups.map(
              (attributeGroup: SchemaTreeAttributeGroup, index: number) => {
                return (
                  <div key={attributeGroup.id} ref={container}>
                    <Draggable
                      draggableId={attributeGroup.id}
                      index={index}
                      isDragDisabled={isDragDropDisabled}
                    >
                      <AttributeGroupRow
                        key={attributeGroup.id}
                        attributeGroup={attributeGroup}
                      />
                    </Draggable>
                  </div>
                );
              },
            )}
          </List>
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default SubItemDetail;
